/**
 * `MsgCreateDeployment` and the whole `GroupSpec` subtree it carries.
 *
 * This is the message that starts a deployment, and it was deliberately left out of
 * `messages.ts` until it could be given generated evidence rather than a careful
 * reading. The reason is the failure mode: an encoding mistake here produces a
 * transaction the chain **accepts**, and the tenant discovers it when a provider
 * refuses the manifest, or — worse — when the deployment is created successfully
 * and is simply not what they asked for. Nothing points back at the encoder.
 *
 * So the test for this file does not compare against `schema.json` alone. It feeds
 * the shipped SDL templates through `sdlToDeployment`, encodes the result here, and
 * asserts **byte equality against `MsgCreateDeployment.Marshal()` run by the real
 * Go implementation** (`tools/sdlref` → `sdl-vectors.json`'s `msgHex`). The input
 * to that comparison is the conversion output that is itself already deep-equal to
 * the Go parser's, so the two halves compose into: our bytes are the chain's bytes.
 *
 * Four things in here are not guessable, and every one of them was found by
 * generating the bytes rather than reading the source:
 *
 *   1. **`DecCoin.amount` is the SCALED integer, not the dotted decimal.** The Go
 *      JSON shows `"10000.000000000000000000"`; the wire carries
 *      `"10000000000000000000000"`. `sdk.LegacyDec` is a gogoproto customtype whose
 *      `Marshal` emits the underlying big.Int, which is the value × 10^18. Sending
 *      the dotted form fails `big.Int` text parsing on the chain — an opaque
 *      unmarshal error nowhere near the price.
 *   2. **`Deposit.sources` is field 5, and it is packed.** The numbering skips 2–4,
 *      and the wire shows `2a 01 01` — tag 5 with wire type 2 — not a bare repeated
 *      varint. Both halves have to be right.
 *   3. **`MsgCreateDeployment` has no depositor field.** Older upstream Akash had one. Here
 *      the same intent is `Deposit.sources` (balance vs. grant), so a `depositor`
 *      would be written as an unknown field and silently dropped.
 *   4. **`Memory` and `Storage` call the field `quantity` in proto and `size` in
 *      JSON.** `convert.ts` follows the JSON, because that is what it is verified
 *      against; this file has to bridge the two names.
 *
 * Two smaller ones, both visible in the reference bytes and both easy to
 * "optimise" away: an empty `SignedBy` is still emitted (`requirements` shows
 * `f1 len=0`), and an endpoint whose kind and sequence number are both zero is
 * emitted as a zero-length message rather than skipped. Dropping either changes the
 * bytes.
 */

import type { GeneratedType } from '@cosmjs/proto-signing'
import { BinaryReader, BinaryWriter } from 'cosmjs-types/binary'

import type { GroupSpecJson } from './convert'
import { DeploymentID } from './messages'

/** Deposit sources, `akash.base.deposit.v1.Source`. */
export const DEPOSIT_SOURCE_INVALID = 0
export const DEPOSIT_SOURCE_BALANCE = 1
export const DEPOSIT_SOURCE_GRANT = 2

/**
 * A `cosmossdk.io/math.LegacyDec` on the wire: the value scaled by 10^18, written
 * as a plain decimal integer with no point.
 *
 * `convert.ts` produces the JSON form — exactly 18 places after a dot — because
 * that is the form the Go parser's JSON output has and therefore the form its
 * conversion is verified against. Scaling is removing the dot, which is exact
 * string arithmetic: no float, no rounding, and no upper bound.
 */
export function decToWire(dotted: string): string {
  const trimmed = dotted.trim()
  const neg = trimmed.startsWith('-')
  const body = neg ? trimmed.slice(1) : trimmed
  const [whole = '0', frac = ''] = body.split('.')
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(frac)) {
    throw new Error(`not a decimal amount: ${dotted}`)
  }
  // Pad or truncate to exactly 18 places, the LegacyDec precision.
  const scaled = `${whole}${frac.padEnd(18, '0').slice(0, 18)}`
  // Strip leading zeros without emptying the string.
  const stripped = scaled.replace(/^0+(?=\d)/, '')
  return neg && stripped !== '0' ? `-${stripped}` : stripped
}

/** `akash.base.resources.v1beta4.ResourceValue` — val is a math.Int, so a string. */
function writeResourceValue(w: BinaryWriter, field: number, val: string): void {
  const inner = BinaryWriter.create()
  if (val !== '') inner.uint32(10).string(val)
  w.uint32((field << 3) | 2).bytes(inner.finish())
}

/** `akash.base.attributes.v1.Attribute` list, written in the order given. */
function writeAttributes(
  w: BinaryWriter,
  field: number,
  attrs: Array<{ key: string; value: string }> | null | undefined,
): void {
  for (const a of attrs ?? []) {
    const inner = BinaryWriter.create()
    if (a.key !== '') inner.uint32(10).string(a.key)
    if (a.value !== '') inner.uint32(18).string(a.value)
    w.uint32((field << 3) | 2).bytes(inner.finish())
  }
}

/**
 * `akash.base.attributes.v1.PlacementRequirements`.
 *
 * `signed_by` is a non-nullable struct in Go, so it is emitted even when both of
 * its lists are null — the reference bytes carry `f1 len=0` for exactly that case.
 */
function writeRequirements(
  w: BinaryWriter,
  field: number,
  req: GroupSpecJson['requirements'],
): void {
  const inner = BinaryWriter.create()

  const signedBy = BinaryWriter.create()
  for (const a of req.signed_by?.all_of ?? []) signedBy.uint32(10).string(a)
  for (const a of req.signed_by?.any_of ?? []) signedBy.uint32(18).string(a)
  inner.uint32(10).bytes(signedBy.finish())

  writeAttributes(inner, 2, req.attributes)
  w.uint32((field << 3) | 2).bytes(inner.finish())
}

/** `akash.base.resources.v1beta4.Resources` — the embedded field 1 of ResourceUnit. */
function writeResources(
  w: BinaryWriter,
  field: number,
  r: GroupSpecJson['resources'][number]['resource'],
): void {
  const inner = BinaryWriter.create()
  if (r.id !== 0) inner.uint32(8).uint32(r.id)

  // CPU: units at field 1.
  const cpu = BinaryWriter.create()
  writeResourceValue(cpu, 1, r.cpu.units.val)
  inner.uint32(18).bytes(cpu.finish())

  // Memory: `quantity` at field 1 — JSON calls it `size`.
  const mem = BinaryWriter.create()
  writeResourceValue(mem, 1, r.memory.size.val)
  inner.uint32(26).bytes(mem.finish())

  // Storage: name at 1, `quantity` at 2 — JSON calls it `size` — attributes at 3.
  // The attributes are what make a volume persistent, so dropping them yields a
  // lease that mounts a volume and loses it on restart.
  for (const s of r.storage ?? []) {
    const st = BinaryWriter.create()
    if (s.name !== '') st.uint32(10).string(s.name)
    writeResourceValue(st, 2, s.size.val)
    writeAttributes(st, 3, s.attributes)
    inner.uint32(34).bytes(st.finish())
  }

  // GPU: units at field 1. Emitted even at zero units — the reference bytes do.
  const gpu = BinaryWriter.create()
  writeResourceValue(gpu, 1, r.gpu.units.val)
  inner.uint32(42).bytes(gpu.finish())

  // Endpoints: kind (enum) at 1, sequence_number at 2. An all-zero endpoint is a
  // zero-length message, NOT a skipped one.
  for (const e of r.endpoints ?? []) {
    const ep = BinaryWriter.create()
    if ((e.kind ?? 0) !== 0) ep.uint32(8).uint32(e.kind ?? 0)
    if (e.sequence_number !== 0) ep.uint32(16).uint32(e.sequence_number)
    inner.uint32(50).bytes(ep.finish())
  }

  w.uint32((field << 3) | 2).bytes(inner.finish())
}

/** `akash.deployment.v1beta4.GroupSpec`. */
export function encodeGroupSpec(g: GroupSpecJson): Uint8Array {
  const w = BinaryWriter.create()
  if (g.name !== '') w.uint32(10).string(g.name)
  writeRequirements(w, 2, g.requirements)

  for (const unit of g.resources) {
    const ru = BinaryWriter.create()
    writeResources(ru, 1, unit.resource)
    if (unit.count !== 0) ru.uint32(16).uint32(unit.count)

    // price: DecCoin. The amount is scaled — see decToWire.
    const price = BinaryWriter.create()
    if (unit.price.denom !== '') price.uint32(10).string(unit.price.denom)
    const amount = decToWire(unit.price.amount)
    if (amount !== '') price.uint32(18).string(amount)
    ru.uint32(26).bytes(price.finish())

    w.uint32(26).bytes(ru.finish())
  }
  return w.finish()
}

/** `akash.base.deposit.v1.Deposit`. */
export interface Deposit {
  /** A `cosmos.base.v1beta1.Coin` — an integer amount, not a Dec. */
  amount: { denom: string; amount: string }
  /** Deposit sources. Field **5**, packed. */
  sources: number[]
}

export interface MsgCreateDeployment {
  id: { owner: string; dseq: bigint }
  groups: GroupSpecJson[]
  /** The manifest version hash — 32 bytes from `manifestVersion()`. */
  hash: Uint8Array
  deposit: Deposit
}

export const MsgCreateDeployment = {
  typeUrl: '/akash.deployment.v1beta4.MsgCreateDeployment',

  encode(m: MsgCreateDeployment, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    w.uint32(10).bytes(DeploymentID.encode(m.id).finish())
    for (const g of m.groups) w.uint32(18).bytes(encodeGroupSpec(g))
    if (m.hash.length !== 0) w.uint32(26).bytes(m.hash)

    const dep = BinaryWriter.create()
    const coin = BinaryWriter.create()
    if (m.deposit.amount.denom !== '') coin.uint32(10).string(m.deposit.amount.denom)
    if (m.deposit.amount.amount !== '') coin.uint32(18).string(m.deposit.amount.amount)
    dep.uint32(10).bytes(coin.finish())
    // Field 5, packed: one length-delimited run of varints, not one tag each.
    const sources = m.deposit.sources.filter((s) => s !== DEPOSIT_SOURCE_INVALID)
    if (sources.length > 0) {
      const packed = BinaryWriter.create()
      for (const s of sources) packed.uint32(s)
      dep.uint32(42).bytes(packed.finish())
    }
    w.uint32(34).bytes(dep.finish())

    return w
  },

  /**
   * Decode is deliberately partial: it reads back the top-level fields the tests
   * assert on. Nothing in the console consumes a `MsgCreateDeployment` off the
   * wire — the chain does — so a full nested decoder would be untested code
   * carrying the same drift risk as the encoder without the reference bytes to
   * pin it.
   */
  decode(input: BinaryReader | Uint8Array, length?: number): MsgCreateDeployment {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m: MsgCreateDeployment = {
      id: { owner: '', dseq: 0n },
      groups: [],
      hash: new Uint8Array(),
      deposit: { amount: { denom: '', amount: '' }, sources: [] },
    }
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.id = DeploymentID.decode(r, r.uint32())
          break
        case 3:
          m.hash = r.bytes()
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: Partial<MsgCreateDeployment>): MsgCreateDeployment {
    return {
      id: o.id ?? { owner: '', dseq: 0n },
      groups: o.groups ?? [],
      hash: o.hash ?? new Uint8Array(),
      deposit: o.deposit ?? { amount: { denom: '', amount: '' }, sources: [] },
    }
  },
}

/**
 * Build the message a tenant sends to create a deployment — the moment the
 * deployment exists on chain and escrow is funded.
 *
 * `groups` and `hash` come from `sdlToDeployment` and `manifestVersion` on the same
 * SDL document; passing a hash computed from a different manifest than the groups
 * describe is the silent failure this whole file is arranged to prevent, which is
 * why they are taken together rather than separately.
 */
export function createDeploymentMessage(args: {
  owner: string
  dseq: bigint
  groups: GroupSpecJson[]
  hash: Uint8Array
  deposit: { denom: string; amount: string }
  sources?: number[]
}) {
  return {
    typeUrl: MsgCreateDeployment.typeUrl,
    value: MsgCreateDeployment.fromPartial({
      id: { owner: args.owner, dseq: args.dseq },
      groups: args.groups,
      hash: args.hash,
      deposit: {
        amount: { denom: args.deposit.denom, amount: args.deposit.amount },
        sources: args.sources ?? [DEPOSIT_SOURCE_BALANCE],
      },
    }),
  }
}

export const deploymentRegistryTypes: ReadonlyArray<[string, GeneratedType]> = [
  [MsgCreateDeployment.typeUrl, MsgCreateDeployment as unknown as GeneratedType],
]
